<template>
  <div v-if="loadingPlans" class="flex justify-center items-center py-12 w-full">
    <inline-svg name="loading" classes="w-12 h-12 animate-spin"></inline-svg>
    <span class="ml-4 text-gray-500">Loading subscriptions...</span>
  </div>
  <form v-else @submit.prevent="handleSubmit">
    <div class="flex flex-wrap gap-8 mb-6 justify-center">
      <label
        v-for="plan in recurringPlans"
        :key="plan.id"
        class="relative flex-1 min-w-[260px] max-w-[340px] w-full sm:w-1/2 md:w-1/3 cursor-pointer flex flex-col items-start p-6 rounded-lg border transition-all duration-200 shadow-md"
        :class="{
          'border-blue-500 bg-blue-50 ring-2 ring-blue-200': isSelected(plan.id),
          'border-gray-300 bg-white hover:border-blue-400 hover:shadow-lg': !isSelected(plan.id)
        }"
        :aria-checked="isSelected(plan.id)"
        role="radio"
        tabindex="0"
      >
        <input
          type="radio"
          name="plan_id"
          :value="plan.id"
          v-model="selectedPlanId"
          class="mb-2 opacity-0 absolute top-4 right-4 h-5 w-5 accent-blue-500"
        />
        <div v-if="plan.product.images?.length" class="w-full flex justify-center mb-2">
          <img :src="plan.product.images[0]" :alt="plan.product.name + ' image'" class="h-16 object-contain" />
        </div>
        <div class="font-bold text-lg mb-1 flex text-center w-full">{{ plan.product.name }}</div>
        <div class="text-gray-600 mb-2">{{ plan.product.description }}</div>
        <ul v-if="plan.product.marketing_features?.length" class="mb-2">
          <li v-for="feature in plan.product.marketing_features" :key="getFeatureKey(feature)" class="flex items-center text-blue-500 mb-1">
            <inline-svg name="check" classes="w-3 h-3 text-green-500 fill-current"></inline-svg>
            <span class="ml-1 text-gray-800">{{ getFeatureName(feature) }}</span>
          </li>
        </ul>
        <div class="font-bold text-xl text-center mb-2 pricing-section">
          {{ formatCurrencyCents(plan.unit_amount, plan.currency) }}
          <span class="font-medium text-sm" v-if="plan.recurring">/ {{ plan.recurring.interval }}</span>
          <span v-else>One-time</span>
        </div>
      </label>
    </div>
    <div class="flex flex-col sm:flex-row justify-between mt-6">
      <button type="button" class="bg-gray-200 px-6 py-2 rounded w-full mb-2 sm:mb-0 sm:w-auto sm:mr-3" @click="$emit('prev')">Back</button>
      <button type="submit" class="bg-blue-600 text-white px-6 py-2 rounded w-full mb-2 sm:mb-0 sm:w-auto sm:mr-3" :disabled="!selectedPlanId">Continue</button>
    </div>
  </form>
</template>

<script>
import InlineSvg from "../InlineSvg";
import axios from 'axios';
import { formatCurrencyCents } from '../../utils/formatters';

export default {
  name: "PlanSelector",
  components: { InlineSvg },
  props: {
    selectedPlan: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      selectedPlanId: this.selectedPlan,
      loadingPlans: false,
      plans: []
    }
  },
  watch: {
    selectedPlan(newVal) {
      this.selectedPlanId = newVal
    }
  },
  computed: {
    recurringPlans() {
      // Filter only recurring plans with category 'plan'
      return this.plans.filter(
        plan =>
          plan.recurring &&
          plan.product &&
          plan.product.metadata?.category === "plan"
      );
    }
  },
  methods: {
    formatCurrencyCents,
    async fetchPlans() {
      this.loadingPlans = true;
      try {
        const res = await axios.get('/signup/stripe-plans');
        this.plans = res.data.data || [];
        this.$emit('plans-loaded', this.plans);
      } catch (error) {
        this.plans = [];
        this.$notify({ type: 'error', title: 'Error', text: 'Could not fetch subscription plans.' });
      } finally {
        this.loadingPlans = false;
      }
    },
    handleSubmit() {
      if (!this.selectedPlanId) {
        this.$notify({
          type: "error",
          title: "Error",
          text: "Please select a plan."
        });
        return;
      }
      this.$emit("plan-selected", this.selectedPlanId);
    },
    isSelected(planId) {
      return this.selectedPlanId === planId;
    },
    getFeatureName(feature) {
      // Handles both string and object
      return typeof feature === "string"
        ? feature
        : feature.name || feature.id || "";
    },
    getFeatureKey(feature) {
      return typeof feature === "string"
        ? feature
        : feature.id || feature.name || JSON.stringify(feature);
    }
  },
  created() {
    this.fetchPlans();
  }
};
</script>
